import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Room } from "@/types/rooms";
import { ContentItem, ContentType } from "@/types/content";
import { ContentGrid } from "@/components/content/ContentGrid";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { PublicAuthOverlay } from "@/components/public/PublicAuthOverlay";
import { useAuth } from "@/hooks/useAuth";
import { usePageMeta } from "@/hooks/usePageMeta";

const PublicRoomPage: React.FC = () => {
  usePageMeta("Shared Room — Shattara AI", "", { noIndex: true });
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [room, setRoom] = useState<Room | null>(null);
  const [contents, setContents] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    const fetchRoom = async () => {
      // Look up room by public_slug
      const { data: roomData, error } = await supabase
        .from("rooms")
        .select("*")
        .eq("public_slug", slug)
        .eq("visibility", "public")
        .maybeSingle();

      if (error || !roomData) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      // If authenticated owner, redirect to the full room page
      if (user && roomData.user_id === user.id) {
        navigate(`/room/${roomData.id}`, { replace: true });
        return;
      }

      setRoom(roomData as Room);

      // Fetch room contents
      const { data: contentData } = await supabase
        .from("content")
        .select("*")
        .eq("room_id", roomData.id)
        .order("created_at", { ascending: false });

      if (contentData) {
        setContents(
          contentData.map((item) => ({
            id: item.id,
            title: item.title,
            type: item.type as ContentType,
            createdAt: item.created_at,
            thumbnailUrl: item.thumbnail_url,
            sourceUrl: item.source_url,
            storagePath: item.storage_path,
            roomId: item.room_id,
            updatedAt: item.updated_at,
            visibility: item.visibility as "private" | "public",
          }))
        );
      }

      setLoading(false);
    };

    fetchRoom();
  }, [slug, user, authLoading, navigate]);

  if (authLoading || loading) {
    return (
      <DashboardShell isPublicView>
        <div className="flex h-[calc(100vh-56px)] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </DashboardShell>
    );
  }

  if (notFound) {
    return (
      <DashboardShell isPublicView>
        <div className="flex h-[calc(100vh-56px)] flex-col items-center justify-center text-center">
          <h2 className="text-2xl font-semibold text-foreground mb-2">Room not found</h2>
          <p className="text-muted-foreground">This room doesn't exist or is not publicly available.</p>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell isPublicView>
      <PublicAuthOverlay />

      <div className="mx-auto w-full px-3 pb-6 pt-6 sm:px-4 lg:px-6">
        {/* Room header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">{room?.name}</h1>
          {room?.description && (
            <p className="mt-2 text-muted-foreground">{room.description}</p>
          )}
        </div>

        {/* Content grid */}
        {contents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <h3 className="mb-2 text-xl font-semibold text-foreground">No content yet</h3>
            <p className="text-muted-foreground">This room doesn't have any content.</p>
          </div>
        ) : (
          <ContentGrid
            items={contents}
            isLoading={false}
            buildHref={(item) => `/content/${item.id}`}
            viewMode="grid"
          />
        )}
      </div>
    </DashboardShell>
  );
};

export default PublicRoomPage;
